/**
 * Mixpanel: what a parent answers and where they stop.
 *
 * One event per screen shown and one per answer, plus the milestones (photo,
 * avatar, paywall, checkout, purchase). The child's name goes with Name
 * Entered and rides on every later event as `childName` (Jernej, 2026-09-07);
 * the photo never leaves the browser. Answers are option ids, not free text.
 *
 * The project token is built in (it is public); VITE_MIXPANEL_TOKEN overrides
 * it at build time for a staging project (see .env.example and .do/app.yaml).
 * EU data residency: set VITE_MIXPANEL_API_HOST=https://api-eu.mixpanel.com.
 */
import mixpanel from 'mixpanel-browser';
import { profileKey } from './profile';

/** The CartoonTime+ project's token (public, it ships in every bundle); the env var overrides it. */
const DEFAULT_TOKEN = 'c46cc8d71195d7b8fcd3aeff8838ac18';
const token = (import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined)?.trim() || DEFAULT_TOKEN;
const apiHost = (import.meta.env.VITE_MIXPANEL_API_HOST as string | undefined)?.trim();
let ready = false;

if (token) {
  try {
    mixpanel.init(token, {
      api_host: apiHost || 'https://api.mixpanel.com',
      persistence: 'localStorage',
      track_pageview: false,
      ignore_dnt: false,
      // Mixpanel Experiments: the SDK fetches this visitor's flag variants on
      // init and tracks `$experiment_started` the first time one is read.
      flags: true,
    });
    mixpanel.register({ app: 'funnel', design: new URLSearchParams(location.search).get('design') || 'v3' });
    ready = true;
  } catch {
    ready = false;
  }
}

type Props = Record<string, unknown>;

/**
 * A profile needs an identity before Mixpanel will store it.
 *
 * `people.set` on an anonymous visitor is QUEUED, not sent: a device id cannot
 * own a profile, so the library holds the update until `identify` runs. That is
 * why the Users table stayed empty while events flowed — the calls were made
 * and then sat in the queue forever.
 *
 * There is no login here, so the device id is promoted to the identity the
 * first time there is anything worth remembering. Done once per page, and never
 * on arrival, so a visitor who bounces off the first screen leaves no profile.
 */
let identified = false;
function ensureProfile(): boolean {
  if (!ready) return false;
  if (identified) return true;
  try {
    mixpanel.identify(mixpanel.get_distinct_id() as string);
    identified = true;
  } catch {
    return false;
  }
  return true;
}

function send(event: string, props?: Props): void {
  if (!ready) return;
  try { mixpanel.track(event, props); } catch { /* analytics never break the funnel */ }
}

/**
 * An event the page may not live long enough to batch: it goes out now, by
 * beacon, which the browser delivers even after the tab is gone.
 */
function sendNow(event: string, props?: Props): void {
  if (!ready) return;
  try { mixpanel.track(event, props, { transport: 'sendBeacon', send_immediately: true }); } catch { /* ignore */ }
}

/** The flags API the SDK exposes at runtime; @types/mixpanel-browser predates it. */
interface Flags {
  get_variant(key: string, fallback: { key: string; value: string }): Promise<{ key?: string; value?: unknown; variant_source?: string }>;
}

export interface Variant {
  value: string;
  /** False when the fallback was served: flags off, blocked, not loaded — the visitor was NOT exposed. */
  assigned: boolean;
}

export const analytics = {
  /**
   * Which variant of an experiment this visitor is in — asked from Mixpanel,
   * which assigns it (sticky per distinct_id) and records the exposure. The
   * fallback is served when flags are off, blocked or slow, and records no
   * exposure, so a visitor who got the fallback is not in the experiment.
   */
  variant: async (key: string, fallback: string): Promise<Variant> => {
    const none = { value: fallback, assigned: false };
    if (!ready) return none;
    try {
      const flags = (mixpanel as unknown as { flags?: Flags }).flags;
      if (!flags) return none;
      const v = await flags.get_variant(key, { key: fallback, value: fallback });
      if (!v || v.variant_source === 'fallback' || typeof v.value !== 'string' || !v.value) return none;
      return { value: v.value, assigned: true };
    } catch {
      return none;
    }
  },
  /** A screen was shown. */
  step: (type: string, key: string | undefined, index: number) => send('Funnel Step Viewed', { step: type, question: key ?? null, index }),
  /**
   * An answer was given: single value or the full multi-select list.
   *
   * It goes to the event stream AND onto the profile. The event says when the
   * parent answered; the profile says what they said, which is what a segment
   * is built from — "everyone who is Catholic and picked bedtime stories" is a
   * profile query, and no amount of event history answers it as directly.
   */
  answer: (question: string, value: string | string[]) => {
    send('Answer Given', { question, answer: value, count: Array.isArray(value) ? value.length : 1 });
    if (!ensureProfile()) return;
    try { mixpanel.people.set({ [profileKey(question)]: value }); } catch { /* ignore */ }
  },
  event: send,
  /** An event that must leave before the page does (a beacon, unbatched). */
  eventNow: sendNow,
  /**
   * Mixpanel's id for this browser, so a stored visit can be lined up with the
   * events it produced. Empty when the library never started (blocked, or no
   * token), which is a row without a join key rather than a failure.
   */
  distinctId: (): string => {
    if (!ready) return '';
    try { return String(mixpanel.get_distinct_id() ?? ''); } catch { return ''; }
  },
  /** Sticky properties on every event from now on (test mode, resumed…). */
  register: (props: Props) => { if (ready) { try { mixpanel.register(props); } catch { /* ignore */ } } },
  /**
   * Write to the user PROFILE — the row in Mixpanel's Users table.
   *
   * `register` does NOT do this, which is the whole reason that table was
   * empty while Events filled up: super properties ride along on events and
   * create nothing. A profile exists only once `people.*` has been called.
   *
   * Called at the moments worth remembering about a visitor — the name, the
   * avatar, the purchase — and deliberately NOT on arrival: a profile for every
   * anonymous page view is a bill rather than a signal.
   */
  person: (props: Props) => {
    if (!ensureProfile()) return;
    try { mixpanel.people.set(props); } catch { /* analytics never break the funnel */ }
  },
  /** Profile values that record the FIRST time something happened, not the last. */
  personOnce: (props: Props) => {
    if (!ensureProfile()) return;
    try { mixpanel.people.set_once(props); } catch { /* ignore */ }
  },
  /**
   * Append to a list on the profile without duplicating — which funnels someone
   * has been through, which milestones they have passed. `set` would overwrite
   * the list each time and lose the visit before.
   */
  personUnion: (props: Record<string, string | string[]>) => {
    if (!ensureProfile()) return;
    try { mixpanel.people.union(props); } catch { /* ignore */ }
  },
};
